import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DollarSign, Search, Package, Building2, Truck, TrendingUp,
  X, ChevronLeft, ChevronRight, ChevronDown,
  Percent, Upload, Download, Edit3, Check, Loader2, AlertCircle,
  Trash2, FileSpreadsheet, CheckSquare
} from 'lucide-react';
import CsvImportModal from '../components/CsvImportModal';

// ─── Constants ──────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const IVA_MAP = { 1: 4, 2: 10, 3: 21 };
const getIvaPercent = (tipoIva) => IVA_MAP[tipoIva] || 21;

const getMargin = (pvp, coste) => {
  const c = Number(coste);
  if (!c || c === 0) return 0;
  return ((Number(pvp) - c) / c) * 100;
};

const getMarginColor = (margin) => {
  if (margin < 0) return 'text-red-600 bg-red-50';
  if (margin < 30) return 'text-yellow-600 bg-yellow-50';
  return 'text-green-600 bg-green-50';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const getAuthHeadersRaw = () => {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}` };
};

const TABS = [
  { key: 'articulo', label: 'Por Artículo', icon: Package },
  { key: 'cliente', label: 'Por Cliente', icon: Building2 },
  { key: 'coste', label: 'Por Proveedor', icon: Truck },
  { key: 'ofertas', label: 'Ofertas', icon: TrendingUp },
];

// ─── Inline Editable Cell ───────────────────────────────────────────────────
const InlineEditCell = ({ value, onSave, suffix = ' €' }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setEditValue(value != null ? String(value) : '');
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const numVal = parseFloat(editValue);
    if (isNaN(numVal) || numVal < 0) { setEditing(false); return; }
    if (numVal === Number(value)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(numVal);
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  if (saving) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-gray-400">
        <Loader2 size={12} className="animate-spin" /> Guardando...
      </span>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="number"
          step="any"
          min="0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-24 px-2 py-1 border border-blue-400 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button
          onMouseDown={(e) => { e.preventDefault(); handleSave(); }}
          className="p-1 text-green-600 hover:bg-green-50 rounded"
        >
          <Check size={14} />
        </button>
      </div>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 cursor-pointer group text-sm"
      onClick={startEdit}
      title="Click para editar"
    >
      {Number(value).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}{suffix}
      <Edit3 size={12} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
    </span>
  );
};

// ─── Reusable Components ────────────────────────────────────────────────────
const SuccessBanner = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="mx-6 mt-4 flex items-center justify-between gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-center gap-2 text-green-700 text-sm">
        <Check size={16} />
        <span>{message}</span>
      </div>
      <button onClick={onDismiss} className="text-green-400 hover:text-green-600">
        <X size={16} />
      </button>
    </div>
  );
};

const ErrorBanner = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="mx-6 mt-4 flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center gap-2 text-red-700 text-sm">
        <AlertCircle size={16} />
        <span>{message}</span>
      </div>
      <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
        <X size={16} />
      </button>
    </div>
  );
};

const Pagination = ({ page, totalPages, total, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
      <span className="text-sm text-gray-500">Mostrando página {page} de {totalPages} ({total} registros)</span>
      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50"
        >
          Anterior
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

const SearchInput = ({ value, onChange, placeholder = 'Buscar...' }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30 focus:border-[#0099FF]"
    />
  </div>
);

const Modal = ({ open, onClose, title, children, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`bg-white rounded-xl shadow-2xl ${wide ? 'max-w-4xl' : 'max-w-2xl'} w-full mx-4 max-h-[80vh] overflow-y-auto`}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 size={32} className="animate-spin text-[#0099FF]" />
    <span className="ml-3 text-gray-500">Cargando...</span>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
    <Package size={48} className="mb-3" />
    <p className="text-sm">{message}</p>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: Por Artículo
// ═══════════════════════════════════════════════════════════════════════════
const TabArticulo = ({ showSuccess, showError }) => {
  // Search & autocomplete
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Product data
  const [product, setProduct] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selection & bulk
  const [selected, setSelected] = useState(new Set());
  const [bulkPercent, setBulkPercent] = useState('');
  const [bulkBase, setBulkBase] = useState('coste');

  // Modals
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [showDescatModal, setShowDescatModal] = useState(false);
  const [descatApplying, setDescatApplying] = useState(false);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autocomplete search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `${API}/prices/product-search?search=${encodeURIComponent(searchTerm)}`,
          { headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error('Error buscando productos');
        const data = await res.json();
        setSuggestions(data.products || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error(err);
        setSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  // Fetch product-clients data
  const fetchProductClients = useCallback(async (raiz) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await fetch(
        `${API}/prices/product-clients?raiz=${encodeURIComponent(raiz)}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Error cargando precios del producto');
      const data = await res.json();
      setProduct(data.product || null);
      setClients(data.clients || []);
    } catch (err) {
      showError(err.message);
      setProduct(null);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleSelectProduct = (prod) => {
    setSearchTerm(prod.raiz);
    setShowSuggestions(false);
    setSuggestions([]);
    fetchProductClients(prod.raiz);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowSuggestions(false);
      fetchProductClients(searchTerm.trim());
    }
  };

  // Selection helpers
  const toggleSelect = (clientId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === clients.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(clients.map(c => c.client_id)));
    }
  };

  // Save single price
  const handleSavePrice = async (client, newValue) => {
    try {
      if (client.price_id) {
        const res = await fetch(`${API}/prices/${client.price_id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ precio: newValue }),
        });
        if (!res.ok) throw new Error('Error guardando precio');
      } else {
        const res = await fetch(`${API}/prices`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ idarticulo: product.id, cmatriz: client.client_id, precio: newValue }),
        });
        if (!res.ok) throw new Error('Error creando precio');
      }
      // Refresh data
      await fetchProductClients(product.raiz);
      showSuccess('Precio actualizado correctamente');
    } catch (err) {
      showError(err.message);
    }
  };

  // Bulk % increase - prepare preview
  const handleBulkApplyClick = () => {
    const pct = parseFloat(bulkPercent);
    if (isNaN(pct) || pct === 0) {
      showError('Introduzca un porcentaje válido');
      return;
    }
    if (selected.size === 0) {
      showError('Seleccione al menos un cliente');
      return;
    }

    const selectedClients = clients.filter(c => selected.has(c.client_id));
    const preview = selectedClients.map(c => {
      const currentPrice = Number(c.precio) || 0;
      let newPrice;
      if (bulkBase === 'coste') {
        const coste = Number(product.precio_coste) || 0;
        newPrice = coste * (1 + pct / 100);
      } else {
        newPrice = currentPrice * (1 + pct / 100);
      }
      newPrice = Math.round(newPrice * 10000) / 10000;
      return {
        ...c,
        currentPrice,
        newPrice,
        increment: newPrice - currentPrice,
      };
    });

    setBulkPreview(preview);
    setShowBulkModal(true);
  };

  const handleBulkConfirm = async () => {
    setBulkApplying(true);
    try {
      for (const item of bulkPreview) {
        if (item.price_id) {
          await fetch(`${API}/prices/${item.price_id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ precio: item.newPrice }),
          });
        } else {
          await fetch(`${API}/prices`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ idarticulo: product.id, cmatriz: item.client_id, precio: item.newPrice }),
          });
        }
      }
      showSuccess(`Precios actualizados para ${bulkPreview.length} clientes`);
      setShowBulkModal(false);
      setBulkPreview([]);
      setSelected(new Set());
      setBulkPercent('');
      await fetchProductClients(product.raiz);
    } catch (err) {
      showError('Error aplicando precios en lote: ' + err.message);
    } finally {
      setBulkApplying(false);
    }
  };

  // Descatalogize
  const handleDescatClick = () => {
    if (selected.size === 0) {
      showError('Seleccione al menos un cliente');
      return;
    }
    setShowDescatModal(true);
  };

  const handleDescatConfirm = async () => {
    setDescatApplying(true);
    const selectedClients = clients.filter(c => selected.has(c.client_id));
    try {
      for (const c of selectedClients) {
        if (c.price_id) {
          await fetch(`${API}/prices/${c.price_id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ precio: 0 }),
          });
        }
      }
      showSuccess(`${selectedClients.length} precios descatalogizados`);
      setShowDescatModal(false);
      setSelected(new Set());
      await fetchProductClients(product.raiz);
    } catch (err) {
      showError('Error descatalogizando: ' + err.message);
    } finally {
      setDescatApplying(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="mb-4 relative" ref={suggestionsRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar Producto por Referencia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30 focus:border-[#0099FF]"
          />
          {searchLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={16} />
          )}
        </div>
        {/* Autocomplete dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelectProduct(s)}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
              >
                <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{s.raiz}</span>
                <span className="text-gray-900">{s.nombre}</span>
              </button>
            ))}
          </div>
        )}
      </form>

      {/* No product selected state */}
      {!product && !loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <Package size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Introduzca una referencia de producto para ver los precios por cliente</p>
            <p className="text-sm mt-1">Escriba al menos 2 caracteres para buscar</p>
          </div>
        </div>
      )}

      {loading && <LoadingSpinner />}

      {/* Product info card + client table */}
      {product && !loading && (
        <>
          {/* Product info card */}
          <div className="mb-4 bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-lg p-4 flex flex-wrap items-center gap-6">
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">Nombre</span>
              <p className="text-sm font-semibold text-gray-900">{product.nombre}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">Referencia</span>
              <p className="text-sm font-mono font-semibold text-gray-900">{product.raiz}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">P. Coste</span>
              <p className="text-sm font-semibold text-gray-900">{Number(product.precio_coste || 0).toFixed(2)} €</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide">IVA</span>
              <p className="text-sm font-semibold text-gray-900">{getIvaPercent(product.tipo_iva)}%</p>
            </div>
          </div>

          {/* Toolbar */}
          <div className="mb-3 flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={clients.length > 0 && selected.size === clients.length}
                onChange={toggleSelectAll}
                className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
              />
              Seleccionar todos
            </label>
            <div className="h-5 w-px bg-gray-300" />
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                placeholder="% incremento"
                value={bulkPercent}
                onChange={(e) => setBulkPercent(e.target.value)}
                className="w-32 px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
              />
              <select
                value={bulkBase}
                onChange={(e) => setBulkBase(e.target.value)}
                className="px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
              >
                <option value="coste">Sobre Coste</option>
                <option value="venta">Sobre Venta</option>
              </select>
              <button
                onClick={handleBulkApplyClick}
                disabled={selected.size === 0}
                className="px-3 py-1.5 bg-[#0099FF] text-white text-sm rounded hover:bg-[#0088ee] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Percent size={14} />
                Aplicar a Seleccionados
              </button>
            </div>
            <div className="h-5 w-px bg-gray-300" />
            <button
              onClick={handleDescatClick}
              disabled={selected.size === 0}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <Trash2 size={14} />
              Descatalogizar
            </button>
            {selected.size > 0 && (
              <span className="text-xs text-gray-500 ml-auto">{selected.size} seleccionado(s)</span>
            )}
          </div>

          {/* Clients table */}
          {clients.length === 0 ? (
            <EmptyState message="No se encontraron clientes para este producto" />
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">
                        <CheckSquare size={14} className="text-gray-400" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Razón Social</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artículo</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P.Coste</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">PVP</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen %</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {clients.map((c) => {
                      const precio = Number(c.precio) || 0;
                      const coste = Number(product.precio_coste) || 0;
                      const margin = getMargin(precio, coste);
                      const marginColor = getMarginColor(margin);
                      const isSelected = selected.has(c.client_id);
                      return (
                        <tr key={c.client_id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(c.client_id)}
                              className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{c.razon_social}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{product.nombre}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right">{coste.toFixed(2)} €</td>
                          <td className="px-4 py-3 text-right">
                            <InlineEditCell
                              value={precio}
                              onSave={(val) => handleSavePrice(c, val)}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${marginColor}`}>
                              {margin.toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleSavePrice(c, precio)}
                              className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium"
                            >
                              Actualizar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Bulk % increase modal */}
      <Modal open={showBulkModal} onClose={() => setShowBulkModal(false)} title="Aplicar incremento de precio" wide>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Se aplicará un incremento del <strong>{bulkPercent}%</strong> sobre{' '}
            <strong>{bulkBase === 'coste' ? 'precio de coste' : 'precio de venta actual'}</strong>{' '}
            a {bulkPreview.length} cliente(s).
          </p>
          <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Precio Actual</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Nuevo Precio</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Incremento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bulkPreview.map((item) => (
                  <tr key={item.client_id}>
                    <td className="px-4 py-2 text-gray-900">{item.razon_social}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{item.currentPrice.toFixed(2)} €</td>
                    <td className="px-4 py-2 text-right font-semibold text-blue-700">{item.newPrice.toFixed(2)} €</td>
                    <td className="px-4 py-2 text-right">
                      <span className={item.increment >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {item.increment >= 0 ? '+' : ''}{item.increment.toFixed(2)} €
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowBulkModal(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleBulkConfirm}
              disabled={bulkApplying}
              className="px-4 py-2 text-sm bg-[#0099FF] text-white rounded-lg hover:bg-[#0088ee] disabled:opacity-50 flex items-center gap-2"
            >
              {bulkApplying && <Loader2 size={14} className="animate-spin" />}
              Confirmar
            </button>
          </div>
        </div>
      </Modal>

      {/* Descatalogize modal */}
      <Modal open={showDescatModal} onClose={() => setShowDescatModal(false)} title="Descatalogizar precios">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              Se pondrá el precio a <strong>0 €</strong> para los siguientes clientes. Esta acción se puede revertir manualmente.
            </p>
          </div>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            <ul className="divide-y divide-gray-200">
              {clients.filter(c => selected.has(c.client_id)).map((c) => (
                <li key={c.client_id} className="px-4 py-2 text-sm text-gray-900">{c.razon_social}</li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowDescatModal(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleDescatConfirm}
              disabled={descatApplying}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {descatApplying && <Loader2 size={14} className="animate-spin" />}
              Descatalogizar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: Por Cliente
// ═══════════════════════════════════════════════════════════════════════════
const TabCliente = ({ showSuccess, showError }) => {
  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchTimer = useRef(null);

  // Prices
  const [prices, setPrices] = useState([]);
  const [priceSearch, setPriceSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const priceSearchTimer = useRef(null);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const allSelected = prices.length > 0 && selected.size === prices.length;

  // Bulk operations
  const [percentage, setPercentage] = useState('');
  const [priceBase, setPriceBase] = useState('coste');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Descatalogizar
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 2) { setClients([]); return; }
    setLoadingClients(true);
    try {
      const res = await fetch(`${API}/prices/clients-search?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Error buscando clientes');
      const data = await res.json();
      setClients(data.clients || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoadingClients(false);
    }
  }, [showError]);

  useEffect(() => {
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => searchClients(clientSearch), 300);
    return () => clearTimeout(clientSearchTimer.current);
  }, [clientSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClientPrices = useCallback(async (clientId, searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/prices/client/${clientId}?search=${encodeURIComponent(searchVal)}&page=${pageVal}&limit=50`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Error cargando precios del cliente');
      const data = await res.json();
      setPrices(data.prices || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (!selectedClient) return;
    fetchClientPrices(selectedClient.id, priceSearch, page);
  }, [selectedClient, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedClient) return;
    if (priceSearchTimer.current) clearTimeout(priceSearchTimer.current);
    priceSearchTimer.current = setTimeout(() => {
      setPage(1);
      fetchClientPrices(selectedClient.id, priceSearch, 1);
    }, 300);
    return () => clearTimeout(priceSearchTimer.current);
  }, [priceSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectClient = (client) => {
    setSelectedClient(client);
    setClients([]);
    setClientSearch('');
    setPriceSearch('');
    setPage(1);
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(prices.map(p => p.id)));
    }
  };

  const handleSavePrice = async (priceId, newValue) => {
    const res = await fetch(`${API}/prices/${priceId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ precio: newValue }),
    });
    if (!res.ok) throw new Error('Error guardando precio');
    setPrices(prev => prev.map(p => p.id === priceId ? { ...p, precio: newValue } : p));
    showSuccess('Precio actualizado correctamente');
  };

  const handleSaveCost = async (productId, newValue) => {
    const res = await fetch(`${API}/prices/by-supplier/${productId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ precio_coste: newValue }),
    });
    if (!res.ok) throw new Error('Error guardando precio de coste');
    setPrices(prev => prev.map(p => p.producto_id === productId ? { ...p, precio_coste: newValue } : p));
    showSuccess('Precio de coste actualizado');
  };

  const prepareBulkIncrease = () => {
    const pct = parseFloat(percentage);
    if (isNaN(pct)) { showError('Introduce un porcentaje válido'); return; }
    if (selected.size === 0) { showError('Selecciona al menos un producto'); return; }

    const preview = prices
      .filter(p => selected.has(p.id))
      .map(p => {
        const base = priceBase === 'coste' ? Number(p.precio_coste || 0) : Number(p.precio || 0);
        const newPrice = base * (1 + pct / 100);
        return {
          ...p,
          oldPrice: Number(p.precio || 0),
          newPrice: Math.round(newPrice * 10000) / 10000,
          increment: Math.round((newPrice - Number(p.precio || 0)) * 100) / 100,
        };
      });
    setBulkPreview(preview);
    setShowBulkModal(true);
  };

  const applyBulkIncrease = async () => {
    setApplyingBulk(true);
    try {
      for (const item of bulkPreview) {
        await fetch(`${API}/prices/${item.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ precio: item.newPrice }),
        });
      }
      showSuccess(`${bulkPreview.length} precios actualizados correctamente`);
      setShowBulkModal(false);
      setPercentage('');
      setSelected(new Set());
      if (selectedClient) fetchClientPrices(selectedClient.id, priceSearch, page);
    } catch (err) {
      showError('Error aplicando cambios: ' + err.message);
    } finally {
      setApplyingBulk(false);
    }
  };

  const handleDescatalogizar = async () => {
    setDeleting(true);
    try {
      const ids = [...selected];
      for (const id of ids) {
        await fetch(`${API}/prices/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
      }
      showSuccess(`${ids.length} productos descatalogizados`);
      setShowDeleteModal(false);
      setSelected(new Set());
      if (selectedClient) fetchClientPrices(selectedClient.id, priceSearch, page);
    } catch (err) {
      showError('Error descatalogizando: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/prices/client/${selectedClient.id}/export`, { headers: getAuthHeadersRaw() });
      if (!res.ok) throw new Error('Error exportando');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `precios_${selectedClient.razon_social || 'cliente'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('Exportación completada');
    } catch (err) {
      showError('Error exportando: ' + err.message);
    }
  };

  // Descarga PDF de precios por cliente (replica el generate_pdf.php del PHP legacy)
  const handleExportPdf = async () => {
    try {
      const res = await fetch(`${API}/prices/client/${selectedClient.id}/export-pdf`, { headers: getAuthHeadersRaw() });
      if (!res.ok) throw new Error('Error exportando PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `precios_${selectedClient.razon_social || 'cliente'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('PDF generado');
    } catch (err) {
      showError('Error exportando PDF: ' + err.message);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/prices/client/${selectedClient.id}/import`, {
        method: 'POST',
        headers: getAuthHeadersRaw(),
        body: formData,
      });
      if (!res.ok) throw new Error('Error importando');
      showSuccess('Importación completada correctamente');
      fetchClientPrices(selectedClient.id, priceSearch, page);
    } catch (err) {
      showError('Error importando: ' + err.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const selectedPrices = prices.filter(p => selected.has(p.id));

  return (
    <div>
      {/* Client search */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar cliente</label>
        <div className="relative">
          <SearchInput
            value={clientSearch}
            onChange={setClientSearch}
            placeholder="Escribe el nombre del cliente..."
          />
          {loadingClients && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 size={16} className="animate-spin text-gray-400" />
            </div>
          )}
          {clients.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {clients.map(c => (
                <button
                  key={c.id}
                  onClick={() => selectClient(c)}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                >
                  <div className="text-sm font-medium text-gray-900">{c.razon_social}</div>
                  {c.clinica_matriz && <div className="text-xs text-gray-500">{c.clinica_matriz}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected client badge */}
      {selectedClient && (
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Building2 size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">{selectedClient.razon_social}</span>
            <button onClick={() => { setSelectedClient(null); setPrices([]); setSelected(new Set()); }}
              className="ml-1 text-blue-400 hover:text-blue-600">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {selectedClient && (
        <div className="mb-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <SearchInput value={priceSearch} onChange={setPriceSearch} placeholder="Buscar artículo..." />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="number"
                step="0.1"
                placeholder="%"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
              />
              <select
                value={priceBase}
                onChange={(e) => setPriceBase(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
              >
                <option value="coste">Coste</option>
                <option value="venta">Venta</option>
              </select>
              <button
                onClick={prepareBulkIncrease}
                disabled={selected.size === 0}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-[#0099FF] text-white rounded-lg hover:bg-[#0077CC] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Percent size={14} />
                Aplicar % a Seleccionados
              </button>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={selected.size === 0}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              Descatalogizar
            </button>

            <button
              onClick={handleExport}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download size={14} />
              Exportar Excel
            </button>

            <button
              onClick={handleExportPdf}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download size={14} />
              Exportar PDF
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Importar CSV
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleImport} />
          </div>
        </div>
      )}

      {/* Prices table */}
      {selectedClient && (
        loading ? <LoadingSpinner /> : prices.length === 0 ? (
          <EmptyState message="No se encontraron precios para este cliente" />
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artículo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P.Coste</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">PVP</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margen %</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IVA %</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">PVP+IVA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {prices.map((p) => {
                  const margin = getMargin(p.precio, p.precio_coste);
                  const marginColor = getMarginColor(margin);
                  const ivaPercent = getIvaPercent(p.tipo_iva);
                  const pvpIva = Number(p.precio || 0) * (1 + ivaPercent / 100);
                  return (
                    <tr key={p.id} className={`hover:bg-gray-50 ${selected.has(p.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleSelect(p.id)}
                          className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{p.producto_raiz || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{p.producto_nombre || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <InlineEditCell
                          value={p.precio_coste}
                          onSave={(val) => handleSaveCost(p.producto_id, val)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <InlineEditCell
                          value={p.precio}
                          onSave={(val) => handleSavePrice(p.id, val)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${marginColor}`}>
                          {margin.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{ivaPercent}%</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{pvpIva.toFixed(2)} €</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        )
      )}

      {!selectedClient && (
        <EmptyState message="Busca y selecciona un cliente para ver sus precios" />
      )}

      {/* Bulk increase modal */}
      <Modal open={showBulkModal} onClose={() => setShowBulkModal(false)} title="Confirmar cambio de precios" wide>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Se aplicará un {percentage}% sobre precio de {priceBase === 'coste' ? 'coste' : 'venta'} a {bulkPreview.length} productos:
          </p>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Precio Actual</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Nuevo Precio</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Incremento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bulkPreview.map(item => (
                  <tr key={item.id}>
                    <td className="px-3 py-2 text-sm text-gray-900">{item.producto_nombre}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 text-right">{item.oldPrice.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-sm text-blue-700 text-right font-medium">{item.newPrice.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-xs font-medium ${item.increment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.increment >= 0 ? '+' : ''}{item.increment.toFixed(2)} €
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={applyBulkIncrease}
            disabled={applyingBulk}
            className="px-4 py-2 text-sm text-white bg-[#0099FF] rounded-lg hover:bg-[#0077CC] disabled:opacity-50 flex items-center gap-2"
          >
            {applyingBulk && <Loader2 size={14} className="animate-spin" />}
            Confirmar
          </button>
        </div>
      </Modal>

      {/* Delete modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Descatalogizar productos">
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            ¿Eliminar los precios de los siguientes {selectedPrices.length} productos?
          </p>
          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
            <ul className="divide-y divide-gray-200">
              {selectedPrices.map(p => (
                <li key={p.id} className="px-3 py-2 text-sm text-gray-900">{p.producto_nombre} ({p.producto_raiz})</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={handleDescatalogizar}
            disabled={deleting}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {deleting && <Loader2 size={14} className="animate-spin" />}
            Descatalogizar
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: Por Proveedor
// ═══════════════════════════════════════════════════════════════════════════
const TabCoste = ({ showSuccess, showError }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const searchTimerRef = useRef(null);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const allSelected = products.length > 0 && selected.size === products.length;

  // Bulk % modal
  const [percentage, setPercentage] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Literal price modal
  const [literalPrice, setLiteralPrice] = useState('');
  const [showLiteralModal, setShowLiteralModal] = useState(false);
  const [applyingLiteral, setApplyingLiteral] = useState(false);

  // Import proveedores CSV modal
  const [showImportProvModal, setShowImportProvModal] = useState(false);

  const fetchProducts = useCallback(async (searchVal, pageVal) => {
    if (!searchVal || searchVal.trim().length === 0) {
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams({ search: searchVal, page: pageVal, limit: 50 });
      const res = await fetch(`${API}/prices/by-supplier?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Error cargando productos');
      const data = await res.json();
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (searched) fetchProducts(search, page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchProducts(search, 1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(products.map(p => p.id)));
  };

  const handleSaveCost = async (productId, newValue) => {
    const res = await fetch(`${API}/prices/by-supplier/${productId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ precio_coste: newValue }),
    });
    if (!res.ok) throw new Error('Error guardando precio de coste');
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, precio_coste: newValue } : p));
    showSuccess('Precio de coste actualizado');
  };

  const prepareBulkIncrease = () => {
    const pct = parseFloat(percentage);
    if (isNaN(pct)) { showError('Introduce un porcentaje válido'); return; }
    if (selected.size === 0) { showError('Selecciona al menos un producto'); return; }

    const preview = products
      .filter(p => selected.has(p.id))
      .map(p => {
        const current = Number(p.precio_coste || 0);
        const newPrice = current * (1 + pct / 100);
        return {
          ...p,
          oldPrice: current,
          newPrice: Math.round(newPrice * 10000) / 10000,
          increment: Math.round((newPrice - current) * 100) / 100,
        };
      });
    setBulkPreview(preview);
    setShowBulkModal(true);
  };

  const applyBulkIncrease = async () => {
    setApplyingBulk(true);
    try {
      const productIds = bulkPreview.map(p => p.id);
      const res = await fetch(`${API}/prices/supplier/bulk-increase`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_ids: productIds, percentage: parseFloat(percentage) }),
      });
      if (!res.ok) throw new Error('Error aplicando incremento');
      showSuccess(`${bulkPreview.length} precios de coste actualizados`);
      setShowBulkModal(false);
      setPercentage('');
      setSelected(new Set());
      fetchProducts(search, page);
    } catch (err) {
      showError('Error: ' + err.message);
    } finally {
      setApplyingBulk(false);
    }
  };

  const prepareLiteralPrice = () => {
    const price = parseFloat(literalPrice);
    if (isNaN(price) || price < 0) { showError('Introduce un precio válido'); return; }
    if (selected.size === 0) { showError('Selecciona al menos un producto'); return; }
    setShowLiteralModal(true);
  };

  const applyLiteralPrice = async () => {
    setApplyingLiteral(true);
    try {
      const productIds = [...selected];
      const res = await fetch(`${API}/prices/supplier/bulk-literal`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_ids: productIds, precio_coste: parseFloat(literalPrice) }),
      });
      if (!res.ok) throw new Error('Error estableciendo precio');
      showSuccess(`Precio establecido para ${productIds.length} productos`);
      setShowLiteralModal(false);
      setLiteralPrice('');
      setSelected(new Set());
      fetchProducts(search, page);
    } catch (err) {
      showError('Error: ' + err.message);
    } finally {
      setApplyingLiteral(false);
    }
  };

  // Asignar proveedor a articulos desde CSV (referencia;proveedor o referencia;nombre;proveedor)
  const handleImportProveedores = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/prices/by-supplier/import-proveedores`, {
      method: 'POST',
      headers: getAuthHeadersRaw(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error al importar proveedores');
    // Refrescar la tabla si hay una busqueda activa para ver los cambios.
    if (searched && search.trim()) fetchProducts(search, page);
    return data;
  };

  const selectedProducts = products.filter(p => selected.has(p.id));

  return (
    <div>
      {/* Search + import */}
      <div className="mb-4 flex items-center gap-2">
        <div className="flex-1">
          <SearchInput value={search} onChange={setSearch} placeholder="Ingrese nombre del proveedor o referencia del producto..." />
        </div>
        <button
          onClick={() => setShowImportProvModal(true)}
          className="flex items-center gap-1 px-3 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
          title="Asignar proveedor a articulos desde un CSV"
        >
          <Upload size={14} />
          Importar Proveedores CSV
        </button>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <input
          type="number"
          step="0.1"
          placeholder="%"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
          className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
        />
        <button
          onClick={prepareBulkIncrease}
          disabled={selected.size === 0}
          className="flex items-center gap-1 px-3 py-2 text-sm bg-[#0099FF] text-white rounded-lg hover:bg-[#0077CC] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Percent size={14} />
          Aplicar % a Seleccionados
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        <input
          type="number"
          step="any"
          placeholder="Precio €"
          value={literalPrice}
          onChange={(e) => setLiteralPrice(e.target.value)}
          className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
        />
        <button
          onClick={prepareLiteralPrice}
          disabled={selected.size === 0}
          className="flex items-center gap-1 px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <DollarSign size={14} />
          Establecer Precio
        </button>

        {selected.size > 0 && (
          <span className="text-xs text-gray-500 ml-2">{selected.size} seleccionado(s)</span>
        )}
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : !searched ? (
        <div className="text-center py-16">
          <Truck size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">Ingrese el nombre del proveedor o referencia para buscar productos</p>
        </div>
      ) : products.length === 0 ? (
        <EmptyState message="No se encontraron productos para esta búsqueda" />
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P.Coste</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IVA %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((p) => (
                <tr key={p.id} className={`hover:bg-gray-50 ${selected.has(p.id) ? 'bg-blue-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{p.raiz || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{p.nombre || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{p.proveedor || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <InlineEditCell
                      value={p.precio_coste}
                      onSave={(val) => handleSaveCost(p.id, val)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 text-right">{getIvaPercent(p.tipo_iva)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
        </div>
      )}

      {/* Bulk % modal */}
      <Modal open={showBulkModal} onClose={() => setShowBulkModal(false)} title="Confirmar incremento de precios de coste" wide>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Se aplicará un {percentage}% a {bulkPreview.length} productos:
          </p>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Precio Actual</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Nuevo Precio</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Incremento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bulkPreview.map(item => (
                  <tr key={item.id}>
                    <td className="px-3 py-2 text-sm text-gray-900">{item.nombre}</td>
                    <td className="px-3 py-2 text-sm text-gray-600 text-right">{item.oldPrice.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-sm text-blue-700 text-right font-medium">{item.newPrice.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-xs font-medium ${item.increment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.increment >= 0 ? '+' : ''}{item.increment.toFixed(2)} €
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={applyBulkIncrease}
            disabled={applyingBulk}
            className="px-4 py-2 text-sm text-white bg-[#0099FF] rounded-lg hover:bg-[#0077CC] disabled:opacity-50 flex items-center gap-2"
          >
            {applyingBulk && <Loader2 size={14} className="animate-spin" />}
            Confirmar
          </button>
        </div>
      </Modal>

      {/* Literal price modal */}
      <Modal open={showLiteralModal} onClose={() => setShowLiteralModal(false)} title="Establecer precio fijo">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
            <AlertCircle size={16} className="text-orange-600" />
            <span className="text-sm text-orange-800">
              Se establecerá el precio de coste a <strong>{Number(literalPrice || 0).toFixed(2)} €</strong> para los siguientes productos:
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
            <ul className="divide-y divide-gray-200">
              {selectedProducts.map(p => (
                <li key={p.id} className="px-3 py-2 text-sm flex justify-between">
                  <span className="text-gray-900">{p.nombre}</span>
                  <span className="text-gray-500">{Number(p.precio_coste || 0).toFixed(2)} € → {Number(literalPrice || 0).toFixed(2)} €</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={() => setShowLiteralModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={applyLiteralPrice}
            disabled={applyingLiteral}
            className="px-4 py-2 text-sm text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
          >
            {applyingLiteral && <Loader2 size={14} className="animate-spin" />}
            Establecer Precio
          </button>
        </div>
      </Modal>

      {/* Import proveedores CSV modal */}
      <CsvImportModal
        isOpen={showImportProvModal}
        onClose={() => setShowImportProvModal(false)}
        title="Importar Proveedores desde CSV"
        description="Formato CSV (delimitador ;): referencia;proveedor  —  o  —  referencia;nombre;proveedor. Asigna el proveedor a cada artículo por su referencia (raíz)."
        onImport={handleImportProveedores}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 4: Ofertas
// ═══════════════════════════════════════════════════════════════════════════
const TabOfertas = ({ showSuccess, showError }) => {
  // Products
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const prodSearchTimer = useRef(null);

  // Selection and quantities
  const [selectedProds, setSelectedProds] = useState(new Set());
  const [quantities, setQuantities] = useState({});
  const [discount, setDiscount] = useState('');

  // Wizard steps
  const [wizardStep, setWizardStep] = useState(0); // 0=closed, 1=select clients, 2=preview, 3=done

  // Clients for step 1
  const [clientSearch, setClientSearch] = useState('');
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [loadingWizardClients, setLoadingWizardClients] = useState(false);
  const clientSearchTimer = useRef(null);

  // Step 2 preview data
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchProducts = useCallback(async (searchVal) => {
    setLoadingProducts(true);
    try {
      const res = await fetch(
        `${API}/prices/by-supplier?search=${encodeURIComponent(searchVal)}&page=1&limit=200`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Error cargando productos');
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoadingProducts(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchProducts('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prodSearchTimer.current) clearTimeout(prodSearchTimer.current);
    prodSearchTimer.current = setTimeout(() => fetchProducts(prodSearch), 300);
    return () => clearTimeout(prodSearchTimer.current);
  }, [prodSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleProd = (id) => {
    setSelectedProds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setQuantities(q => { const n = { ...q }; delete n[id]; return n; });
      } else {
        next.add(id);
        setQuantities(q => ({ ...q, [id]: 1 }));
      }
      return next;
    });
  };

  const updateQuantity = (id, val) => {
    setQuantities(q => ({ ...q, [id]: Math.max(1, parseInt(val) || 1) }));
  };

  // Step 1: search clients
  const searchWizardClients = useCallback(async (q) => {
    if (!q || q.length < 1) {
      // Load some default clients
      setLoadingWizardClients(true);
      try {
        const res = await fetch(`${API}/prices/clients-search?search=`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setAvailableClients(data.clients || []);
        }
      } catch (err) { /* ignore */ }
      setLoadingWizardClients(false);
      return;
    }
    setLoadingWizardClients(true);
    try {
      const res = await fetch(`${API}/prices/clients-search?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAvailableClients(data.clients || []);
      }
    } catch (err) { /* ignore */ }
    setLoadingWizardClients(false);
  }, []);

  useEffect(() => {
    if (wizardStep !== 1) return;
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => searchWizardClients(clientSearch), 300);
    return () => clearTimeout(clientSearchTimer.current);
  }, [clientSearch, wizardStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleClient = (id) => {
    setSelectedClients(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openWizard = () => {
    if (selectedProds.size === 0) { showError('Selecciona al menos un producto'); return; }
    const pct = parseFloat(discount);
    if (isNaN(pct) || pct <= 0) { showError('Introduce un descuento válido'); return; }
    setWizardStep(1);
    setClientSearch('');
    setSelectedClients(new Set());
    searchWizardClients('');
  };

  const goToPreview = async () => {
    if (selectedClients.size === 0) { showError('Selecciona al menos un cliente'); return; }
    setLoadingPreview(true);
    setWizardStep(2);

    const pct = parseFloat(discount);
    const selectedProductsList = products.filter(p => selectedProds.has(p.id));
    const clientIds = [...selectedClients];
    const clientList = availableClients.filter(c => selectedClients.has(c.id));

    const preview = [];
    for (const client of clientList) {
      try {
        const res = await fetch(`${API}/prices/client/${client.id}?limit=1000`, { headers: getAuthHeaders() });
        if (!res.ok) continue;
        const data = await res.json();
        const clientPrices = data.prices || [];

        const items = selectedProductsList.map(prod => {
          const clientPrice = clientPrices.find(cp => cp.idarticulo === prod.id);
          const currentPrice = clientPrice ? Number(clientPrice.precio) : 0;
          const offerPrice = currentPrice > 0 ? currentPrice * (1 - pct / 100) : 0;
          const qty = quantities[prod.id] || 1;
          return {
            product: prod.nombre,
            ref: prod.raiz,
            quantity: qty,
            currentPrice,
            offerPrice: Math.round(offerPrice * 10000) / 10000,
            total: Math.round(offerPrice * qty * 100) / 100,
          };
        });

        preview.push({
          client: client.razon_social,
          clientId: client.id,
          items,
        });
      } catch (err) {
        console.error('Error loading prices for client', client.id, err);
      }
    }

    setPreviewData(preview);
    setLoadingPreview(false);
  };

  const handleSendOffers = () => {
    showSuccess('Funcionalidad de envío en desarrollo. Las ofertas se han generado correctamente.');
    setWizardStep(0);
  };

  const hasValidSelection = selectedProds.size > 0 && discount && parseFloat(discount) > 0;

  return (
    <div>
      {/* Discount input */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Descuento %:</label>
        <input
          type="number"
          step="0.1"
          min="0"
          max="100"
          placeholder="Ej: 10"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
        />
        <button
          onClick={openWizard}
          disabled={!hasValidSelection}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-[#0099FF] text-white rounded-lg hover:bg-[#0077CC] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <TrendingUp size={14} />
          Enviar Oferta
        </button>
        {selectedProds.size > 0 && (
          <span className="text-xs text-gray-500">{selectedProds.size} producto(s) seleccionado(s)</span>
        )}
      </div>

      {/* Products search */}
      <div className="mb-4">
        <SearchInput value={prodSearch} onChange={setProdSearch} placeholder="Buscar productos..." />
      </div>

      {/* Products table */}
      {loadingProducts ? <LoadingSpinner /> : products.length === 0 ? (
        <EmptyState message="No se encontraron productos" />
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referencia</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cantidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((p) => {
                const isSelected = selectedProds.has(p.id);
                return (
                  <tr key={p.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleProd(p.id)}
                        className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{p.raiz || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{p.nombre || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="number"
                        min="1"
                        value={quantities[p.id] || ''}
                        onChange={(e) => updateQuantity(p.id, e.target.value)}
                        disabled={!isSelected}
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0099FF]/30"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Step 1: Select Clients */}
      <Modal open={wizardStep === 1} onClose={() => setWizardStep(0)} title="Paso 1: Seleccionar Clientes" wide>
        <div className="px-6 pt-4">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#0099FF] text-white font-medium text-xs">1</span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-500 font-medium text-xs">2</span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-500 font-medium text-xs">3</span>
          </div>

          <SearchInput value={clientSearch} onChange={setClientSearch} placeholder="Buscar clientes..." />
        </div>

        <div className="p-6">
          {loadingWizardClients ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {availableClients.map(c => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedClients.has(c.id) ? 'border-[#0099FF] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedClients.has(c.id)}
                    onChange={() => toggleClient(c.id)}
                    className="rounded border-gray-300 text-[#0099FF] focus:ring-[#0099FF]"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{c.razon_social}</div>
                    {c.clinica_matriz && <div className="text-xs text-gray-500">{c.clinica_matriz}</div>}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button onClick={() => setWizardStep(0)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={goToPreview}
            disabled={selectedClients.size === 0}
            className="px-4 py-2 text-sm text-white bg-[#0099FF] rounded-lg hover:bg-[#0077CC] disabled:opacity-50"
          >
            Siguiente: Revisar Precios
          </button>
        </div>
      </Modal>

      {/* Step 2: Price Preview */}
      <Modal open={wizardStep === 2} onClose={() => setWizardStep(0)} title="Paso 2: Revisar Precios" wide>
        <div className="px-6 pt-4">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-500 text-white font-medium text-xs">✓</span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#0099FF] text-white font-medium text-xs">2</span>
            <span className="text-gray-400">→</span>
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-500 font-medium text-xs">3</span>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">Descuento: {discount}%</span>
            <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded">{selectedClients.size} cliente(s)</span>
            <span className="px-2 py-1 bg-green-50 text-green-700 rounded">{selectedProds.size} producto(s)</span>
          </div>
        </div>

        <div className="p-6 max-h-96 overflow-y-auto">
          {loadingPreview ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            previewData.map((clientData, idx) => (
              <div key={clientData.clientId} className={`${idx > 0 ? 'mt-6' : ''}`}>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Building2 size={14} className="text-gray-400" />
                  {clientData.client}
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ref</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Cant.</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Precio Actual</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Precio Oferta</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {clientData.items.map((item, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-sm text-gray-900">{item.product}</td>
                          <td className="px-3 py-2 text-sm text-gray-500 font-mono">{item.ref}</td>
                          <td className="px-3 py-2 text-sm text-gray-600 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">{item.currentPrice.toFixed(2)} €</td>
                          <td className="px-3 py-2 text-sm text-blue-700 text-right font-medium">{item.offerPrice.toFixed(2)} €</td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{item.total.toFixed(2)} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button onClick={() => setWizardStep(1)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Volver
          </button>
          <button
            onClick={handleSendOffers}
            disabled={loadingPreview}
            className="px-4 py-2 text-sm text-white bg-[#0099FF] rounded-lg hover:bg-[#0077CC] disabled:opacity-50"
          >
            Enviar Ofertas
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const PriceManagement = () => {
  const [activeTab, setActiveTab] = useState('articulo');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successTimer = useRef(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCmatriz, setImportCmatriz] = useState('');

  const handleImportPrices = async (file) => {
    if (!importCmatriz) throw new Error('Debe ingresar el ID del cliente matriz');
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/prices/import-csv?cmatriz=${encodeURIComponent(importCmatriz)}`, {
      method: 'POST',
      headers: getAuthHeadersRaw(),
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error al importar');
    return data;
  };

  const showError = useCallback((msg) => {
    setError(msg);
    setSuccess('');
  }, []);

  const showSuccess = useCallback((msg) => {
    setSuccess(msg);
    setError('');
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccess(''), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    };
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case 'articulo':
        return <TabArticulo showSuccess={showSuccess} showError={showError} />;
      case 'cliente':
        return <TabCliente showSuccess={showSuccess} showError={showError} />;
      case 'coste':
        return <TabCoste showSuccess={showSuccess} showError={showError} />;
      case 'ofertas':
        return <TabOfertas showSuccess={showSuccess} showError={showError} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign size={24} className="text-[#0099FF]" />
              Gestión de Precios
            </h1>
            <p className="text-sm text-gray-500 mt-1">Administra precios por artículo, cliente, coste y ofertas</p>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Upload size={16} /> Importar Precios CSV
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
        <div className="flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-[#0099FF] text-[#0099FF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Banners */}
      <ErrorBanner message={error} onDismiss={() => setError('')} />
      <SuccessBanner message={success} onDismiss={() => setSuccess('')} />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {renderTab()}
        </div>
      </div>

      {/* CSV Import Modal for Prices */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => { setShowImportModal(false); setImportCmatriz(''); }}
        title="Importar Precios desde CSV"
        description="Formato CSV (delimitador ;): codigo;nombre;precio — una fila por producto"
        onImport={handleImportPrices}
        extraFields={
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ID Cliente Matriz *</label>
            <input
              type="text"
              value={importCmatriz}
              onChange={(e) => setImportCmatriz(e.target.value)}
              placeholder="Ej: 123"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {!importCmatriz && <p className="text-xs text-amber-600 mt-1">Debe ingresar el ID del cliente matriz antes de importar</p>}
          </div>
        }
      />
    </div>
  );
};

export default PriceManagement;
